import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Input from '@/src/components/atom/Input';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import FormContent from '@/src/components/molecule/FormContent';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import SectionCard from '@/src/components/molecule/SectionCard';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { TOAST_CONST } from '@/src/constants/toast';
import { useToast } from '@/src/hooks/useToast';
import PATH from '@/src/routes/path';
import { useMutation } from '@tanstack/react-query';
import { useController, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import RadioGroup from '@/components/atom/RadioGroup';
import {
  DEFAULT_VALUES,
  formFields,
  formSchema,
} from '@/src/pages/RecyclingProducts/upsertField';
import { postRecyclingProductAPI } from '@/src/apis/rest/recyclingProducts';
import { UpsertRecyclingProductRequestBody } from '@/src/apis/rest/recyclingProducts.dtos';
import { NAV_CONST } from '@/src/constants/nav';
import ImageUpload from '@/components/atom/ImageUpload';
import IconButton from '@/components/atom/Button/IconButton';
import { useCallback, useMemo, useRef } from 'react';
import FormTable from '@/src/pages/RecyclingProducts/components/FormTable';
import { optionFormColumn } from '@/src/pages/RecyclingProducts/components/optionFormColumn';
import { AgGridReact } from 'ag-grid-react';
import { Media } from '@/src/models/MeidaDto';
import { usePrompt } from '@/hooks/usePrompt';
import ImageUploadInfo from '@/components/molecule/ImageUploadInfo';
import { OPTIONS_CONST } from '@/src/constants/options';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';

const BACK_PATH = PATH.RECYCLING_PRODUCT_LIST;

function RecyclingProductCreatePage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const gridRef = useRef<AgGridReact>(null);

  const { Dialog, isOpen, openDialog, closeDialog } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);

  const form = useForm<
    UpsertRecyclingProductRequestBody & {
      productMedia: Media;
    }
  >({
    resolver: zodResolver(formSchema()),
    defaultValues: DEFAULT_VALUES,
  });

  const watchedFields = useWatch({
    control: form.control,
  });

  const { field: mediaField } = useController({
    name: 'productMedia',
    control: form.control,
  });

  const postRecyclingProduct = useMutation({
    mutationFn: postRecyclingProductAPI,
  });

  const optionColDefs = useMemo(() => {
    return optionFormColumn({
      form,
      tableField: 'recyclingProductOptions',
    });
  }, [form]);

  const isFormValid = useMemo(() => {
    const options = watchedFields?.recyclingProductOptions;
    if (!form.formState.isValid || !options || options.length === 0)
      return false;

    for (const option of options) {
      if (
        !option.size ||
        !option.weight ||
        !option.carbonEmissionReduction ||
        !option.pineScore
      ) {
        return false;
      }
    }

    return true;
  }, [form, watchedFields]);

  // event
  const handleSubmit = useCallback(
    (values: UpsertRecyclingProductRequestBody) => {
      postRecyclingProduct.mutate(values, {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
          });

          navigate(BACK_PATH);
        },
        onError: e => {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        },
      });
    },
    [navigate, postRecyclingProduct, toast],
  );

  usePrompt(form.formState.isDirty && !form.formState.isSubmitted);

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={() => {
              navigate(-1);
            }}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <Button
            disabled={!isFormValid}
            type="button"
            onClick={() => openDialog()}
            className="w-40"
            buttonSize="medium"
            isLoading={postRecyclingProduct.isPending}
          >
            <div className="flex items-center gap-x-2">{BUTTON_CONST.save}</div>
          </Button>
        </div>
      </BottomFixedContainer>
    );
  };

  return (
    <>
      <MainContentTitle
        breadcrumbList={[
          NAV_CONST.recyclingProductManagement,
          NAV_CONST.recyclingProductAdd,
        ]}
        pageTitle={NAV_CONST.recyclingProductAdd}
      />
      <Form {...form}>
        <form
          ref={formRef}
          className="relative mb-[100px] flex flex-col gap-6 "
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <button type="submit" className="hidden" />
          <SectionCard cardTitle="수거 품목 정보">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-x-6">
              <div className="flex flex-col gap-y-6">
                <FormField
                  control={form.control}
                  name={formFields.category.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="수거 품목 명"
                        isRequired
                        error={error}
                      >
                        <Input
                          containerClassName="flex-1"
                          placeholder="품목 명을 입력해주세요"
                          onClear={() => field.onChange('')}
                          {...field}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name={formFields.isExposed.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="노출 여부" isRequired error={error}>
                        <RadioGroup
                          optionList={[
                            { label: OPTIONS_CONST.exposed, value: 'true' },
                            { label: OPTIONS_CONST.notExposed, value: 'false' },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: e => field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name={formFields.isEventProduct.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="이벤트 여부" isRequired error={error}>
                        <RadioGroup
                          optionList={[
                            { label: OPTIONS_CONST.applied, value: 'true' },
                            { label: OPTIONS_CONST.notApplied, value: 'false' },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: e => field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
              </div>
              <div className="border-r border-r-gray1" />
              <div className="flex flex-col gap-y-6">
                <FormField
                  control={form.control}
                  name="productMediaId"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="품목 이미지"
                        isRequired
                        error={error}
                        defaultMessage={
                          <ImageUploadInfo widthSize={335} heightSize={335} />
                        }
                      >
                        <ImageUpload
                          width={128}
                          height={128}
                          widthText={335}
                          heightText={335}
                          maxImageSizeMb={5}
                          imageClassName="object-cover"
                          value={mediaField?.value?.bufferFullUrl}
                          onChange={e => {
                            field.onChange(e.id);
                            mediaField.onChange(e);
                          }}
                          topAccessary={({ handleEdit, handleRemove }) => {
                            return (
                              <>
                                <div className="flex gap-x-1">
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'Pencil' }}
                                    onClick={() => {
                                      handleEdit();
                                    }}
                                  />
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'X' }}
                                    onClick={() => {
                                      handleRemove();
                                      field.onChange(undefined);
                                    }}
                                  />
                                </div>
                              </>
                            );
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
              </div>
            </div>
          </SectionCard>
          <SectionCard
            cardTitle="수거 품목 옵션 정보"
            contentProps={{ className: 'px-0 mt-4' }}
          >
            <FormTable
              form={form}
              ref={gridRef}
              tableField="recyclingProductOptions"
              columnDefs={optionColDefs}
            />
          </SectionCard>
          {renderBottomFixedContent()}
        </form>
      </Form>
      {/* 모달 */}
      {isOpen && (
        <Dialog
          open={isOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openDialog();
            } else {
              closeDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.save}
          isLoading={postRecyclingProduct.isPending}
        />
      )}
    </>
  );
}

export default RecyclingProductCreatePage;
